import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Wrench, IndianRupee, Truck, Award, Phone, Construction, Mountain, Building2, HardHat, CheckCircle2, ChevronLeft, ChevronRight, PackageOpen, PlayCircle, Quote, Briefcase, Download } from "lucide-react";
import Marquee from "react-fast-marquee";
import { motion, AnimatePresence, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { mediaUrl } from "@/lib/media";
import ProductCard from "@/components/ProductCard";
import { Section, SectionTitle } from "@/components/Section";

const ICON_MAP = { 0: Construction, 1: Mountain, 2: Building2, 3: HardHat };
const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

// Highlight ONE word of a title in saffron — picks the strongest impact word.
function highlightTitle(text) {
  if (!text) return null;
  const words = text.split(/\s+/);
  // pick the longest meaningful word (skip very short ones)
  let bestIdx = 0, bestLen = 0;
  words.forEach((w, i) => {
    const clean = w.replace(/[^A-Za-z]/g, "");
    if (clean.length > bestLen && clean.length >= 5) { bestLen = clean.length; bestIdx = i; }
  });
  return words.map((w, i) => i === bestIdx
    ? <span key={i} className="text-[#E65C00]">{w} </span>
    : <span key={i}>{w} </span>);
}

function HeroCarousel({ slides }) {
  const [idx, setIdx] = useState(0);
  const list = slides && slides.length > 0 ? slides : [];

  useEffect(() => {
    if (list.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), 6000);
    return () => clearInterval(t);
  }, [list.length]);

  // Empty state — clean black + subtle grid texture, no fallback photo
  if (list.length === 0) {
    return (
      <div className="absolute inset-0" data-testid="hero-carousel-empty">
        <div className="absolute inset-0 bg-[#1A1A1A]" />
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0 noise-overlay" />
      </div>
    );
  }

  return (
    <div className="absolute inset-0" data-testid="hero-carousel">
      <AnimatePresence mode="wait">
        <motion.img
          key={idx}
          src={mediaUrl(list[idx])}
          alt=""
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 0.55, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2 }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/95 via-[#1A1A1A]/70 to-[#1A1A1A]/30" />
      <div className="absolute inset-0 grid-bg opacity-20" />

      {list.length > 1 && (
        <>
          <div className="absolute bottom-6 right-6 z-20 flex items-center gap-2" data-testid="hero-carousel-dots">
            {list.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-1.5 transition-all ${i === idx ? "w-10 bg-[#E65C00]" : "w-5 bg-white/40 hover:bg-white/70"}`}
              />
            ))}
          </div>
          <button
            onClick={() => setIdx((i) => (i - 1 + list.length) % list.length)}
            aria-label="Previous slide"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 hidden md:flex items-center justify-center bg-black/30 hover:bg-[#E65C00] backdrop-blur text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setIdx((i) => (i + 1) % list.length)}
            aria-label="Next slide"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 hidden md:flex items-center justify-center bg-black/30 hover:bg-[#E65C00] backdrop-blur text-white transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}
    </div>
  );
}

function EmptyState({ icon: Icon = PackageOpen, title, sub }) {
  return (
    <div className="col-span-full border-2 border-dashed border-gray-300 bg-gray-50 py-16 px-6 text-center" data-testid="home-empty-state">
      <Icon className="w-10 h-10 mx-auto text-gray-400" />
      <div className="font-heading font-black uppercase text-lg mt-4 text-gray-700">{title}</div>
      <div className="text-sm text-gray-500 mt-2 max-w-md mx-auto">{sub}</div>
    </div>
  );
}

/**
 * AnimatedNumber — counts up from 0 → final number when scrolled into view.
 * Preserves the prefix/suffix (e.g. "₹" / "+" / "Cr+") around the digits.
 */
function AnimatedNumber({ value, duration = 1.6 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(value);

  // Parse: prefix (non-digit) + numericPart + suffix (everything after digits)
  const match = String(value || "").match(/^(\D*)(\d+(?:\.\d+)?)(.*)$/);
  const prefix = match ? match[1] : "";
  const target = match ? Number(match[2]) : 0;
  const suffix = match ? match[3] : "";
  const isInt = match && !match[2].includes(".");

  useEffect(() => {
    if (!inView || !match) return;
    const controls = animate(mv, target, {
      duration,
      ease: "easeOut",
      onUpdate: (v) => {
        const n = isInt ? Math.round(v) : v.toFixed(1);
        setDisplay(`${prefix}${n.toLocaleString ? n.toLocaleString("en-IN") : n}${suffix}`);
      },
    });
    return () => controls.stop();
  }, [inView]);

  return <span ref={ref} data-testid="animated-number">{display}</span>;
}

// Convert a YouTube/Vimeo URL to embed form. Else treat as direct video URL.
function videoEmbed(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:.*v=|.*\/|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}?rel=0&modestbranding=1` };
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return { type: "iframe", src: `https://player.vimeo.com/video/${vm[1]}` };
  return { type: "video", src: mediaUrl(url) };
}

function CorporateVideoBand({ settings }) {
  const url = settings?.corporate_video_url;
  const profileUrl = settings?.corporate_profile_url;
  // Hide ONLY if BOTH video and profile are missing — otherwise we still want to show download.
  if (!url && !profileUrl) return null;
  const embed = url ? videoEmbed(url) : null;
  return (
    <section className="bg-[#0F0F0F] text-white py-20 relative overflow-hidden noise-overlay" data-testid="home-corporate-video">
      <div className="absolute inset-0 grid-bg opacity-15 pointer-events-none" />
      <Section className="relative">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="label-eyebrow !text-[#FFCC00]">{settings?.corporate_video_eyebrow || "OUR STORY"}</div>
          <h2 className="heading-xl text-3xl sm:text-4xl lg:text-5xl mt-4 text-white">
            {highlightTitle(settings?.corporate_video_title || "WATCH WHAT WE BUILD.")}
          </h2>
          {settings?.corporate_video_subtitle && (
            <p className="mt-5 text-gray-400 text-base sm:text-lg leading-relaxed">{settings.corporate_video_subtitle}</p>
          )}
        </div>
        {embed && (
          <motion.div {...fadeUp} className="relative max-w-5xl mx-auto aspect-video bg-black border-4 border-[#E65C00] shadow-2xl">
            {embed.type === "iframe" ? (
              <iframe
                src={embed.src}
                title="Corporate Video"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="corporate-video-iframe"
              />
            ) : (
              <video controls src={embed.src} className="absolute inset-0 w-full h-full" data-testid="corporate-video-tag" />
            )}
            <div className="absolute -top-3 -left-3 bg-[#FFCC00] px-3 py-1 font-heading font-black text-[#1A1A1A] text-xs uppercase tracking-widest">
              <PlayCircle className="w-3 h-3 inline mr-1" /> Corporate Film
            </div>
          </motion.div>
        )}
        {profileUrl && (
          <div className="mt-10 text-center">
            <a
              href={mediaUrl(profileUrl)}
              target="_blank"
              rel="noreferrer"
              download
              className="inline-flex items-center gap-2 bg-[#FFCC00] text-[#1A1A1A] font-heading font-black uppercase tracking-widest text-xs px-6 py-3 hover:bg-[#E65C00] hover:text-white transition-colors"
              data-testid="corporate-profile-download"
            >
              <Download className="w-4 h-4" /> Download Corporate Profile (PDF)
            </a>
          </div>
        )}
      </Section>
    </section>
  );
}

function TestimonialsBand({ settings }) {
  const [items, setItems] = useState(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  if (items === null || items.length === 0) return null;  // hide entirely when no testimonials

  // Duplicate so the marquee loops seamlessly
  const loop = [...items, ...items];

  return (
    <section className="bg-[#FFCC00]/10 py-20 relative overflow-hidden" data-testid="home-testimonials">
      <Section>
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="label-eyebrow">{settings?.testimonials_eyebrow || "CUSTOMER STORIES"}</div>
          <h2 className="heading-xl text-3xl sm:text-4xl lg:text-5xl mt-4">
            {highlightTitle(settings?.testimonials_title || "WHAT OUR PARTNERS SAY.")}
          </h2>
        </div>
      </Section>

      <div
        className="testimonials-marquee relative"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div
          className="flex gap-6 px-6 py-4"
          style={{
            animation: `testimonial-scroll ${Math.max(20, items.length * 12)}s linear infinite`,
            animationPlayState: paused ? "paused" : "running",
            width: "max-content",
          }}
        >
          {loop.map((t, i) => (
            <article
              key={`${t.id}-${i}`}
              className="shrink-0 w-[340px] sm:w-[400px] bg-white p-7 border-2 border-[#1A1A1A] shadow-lg relative hover:shadow-2xl transition-shadow"
              data-testid={`testimonial-card-${t.id}-${i}`}
            >
              <Quote className="w-8 h-8 text-[#E65C00]/40 absolute top-4 right-4" />
              <p className="text-sm text-gray-800 leading-relaxed italic line-clamp-6 min-h-[120px]">
                "{t.quote}"
              </p>
              <div className="mt-5 flex items-center gap-4 pt-4 border-t border-gray-200">
                {t.photo ? (
                  <img
                    src={mediaUrl(t.photo)}
                    alt={t.name}
                    className="w-14 h-14 rounded-full object-cover border-4 border-[#FFCC00] shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#1A1A1A] text-[#E65C00] flex items-center justify-center font-heading font-black text-lg border-4 border-[#FFCC00] shrink-0">
                    {t.name?.[0] || "T"}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-heading font-black text-[#1A1A1A] uppercase text-sm truncate">{t.name}</div>
                  <div className="text-xs text-gray-600 truncate">
                    {t.role}{t.role && t.company ? " · " : ""}{t.company}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      <div className="text-center text-xs text-gray-500 mt-4 italic">Hover any card to pause</div>
    </section>
  );
}

export default function Home() {
  const { settings } = useSettings();
  const [featured, setFeatured] = useState(null); // null = loading; [] = empty
  const [latestBlogs, setLatestBlogs] = useState(null);

  useEffect(() => {
    api.get("/products?featured=true").then((r) => setFeatured(r.data.slice(0, 6))).catch(() => setFeatured([]));
    api.get("/blogs").then((r) => setLatestBlogs(r.data.slice(0, 3))).catch(() => setLatestBlogs([]));
  }, []);

  const fallbackServices = [
    { icon: Truck, title: "Buy New & Used", desc: "Curated inventory from India's top OEMs. 87-point inspection on used equipment." },
    { icon: IndianRupee, title: "Finance Solutions", desc: "Best EMI deals from 8+ NBFC & bank partners. From 9.5% p.a." },
    { icon: Wrench, title: "Service & AMC", desc: "On-site service across Bihar. Authorised Bull Machines support." },
    { icon: ShieldCheck, title: "Insurance Cover", desc: "Comprehensive equipment insurance with quick claim settlement." },
  ];
  const serviceIcons = [Truck, IndianRupee, Wrench, ShieldCheck, Briefcase, Award];
  const services = (settings?.home_services_items && settings.home_services_items.length > 0)
    ? settings.home_services_items.map((s, i) => ({ ...s, icon: serviceIcons[i % serviceIcons.length] }))
    : fallbackServices;

  const heroSlides = settings?.hero_slides && settings.hero_slides.length > 0 ? settings.hero_slides : [];
  const marquee = settings?.marquee_items && settings.marquee_items.length > 0 ? settings.marquee_items : ["BULL MACHINES", "JCB", "TATA HITACHI", "VOLVO", "CATERPILLAR", "BEML", "SCHWING STETTER", "TATA MOTORS"];
  const apps = settings?.applications_items && settings.applications_items.length > 0 ? settings.applications_items : ["Road & Highway Construction", "Mining & Quarrying", "Urban Infrastructure", "Industrial & Factory Setup"];
  const aboutImage = settings?.about_image || "";

  return (
    <>
      {/* ============ HERO ============ */}
      <section className="relative bg-[#1A1A1A] text-white overflow-hidden noise-overlay" data-testid="home-hero">
        <HeroCarousel slides={heroSlides} />

        <div className="relative max-w-7xl mx-auto px-6 py-28 lg:py-40 z-10">
          <motion.div {...fadeUp} className="max-w-4xl">
            <div className="inline-flex items-center gap-2 border border-[#E65C00]/40 bg-[#E65C00]/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-6">
              <span className="w-1.5 h-1.5 bg-[#FFCC00] inline-block" /> {settings?.hero_badge_text || "AUTHORISED BULL MACHINES DEALER · EST. 2016"}
            </div>
            {/* Brand name — slightly smaller, still single line */}
            <h1
              className="font-heading font-black text-[#E65C00] uppercase leading-[0.9] tracking-tight text-4xl sm:text-5xl lg:text-7xl whitespace-nowrap"
              style={{ fontStretch: "extra-condensed", letterSpacing: "-0.02em" }}
              data-testid="hero-brand-name"
            >
              {settings?.hero_brand_name || "TARA AUTOHUB"}
            </h1>
            <div className="heading-xl text-3xl sm:text-4xl lg:text-5xl text-white mt-5" data-testid="hero-title">
              {settings?.hero_title_line1 || "HEAVY MACHINERY."}<br />
              <span className="text-white/70">{settings?.hero_title_line2 || "BUILT FOR INDIA."}</span>
            </div>
            <p className="mt-7 text-lg sm:text-xl text-gray-300 max-w-2xl leading-relaxed">
              {settings?.hero_subtitle || "Buy. Sell. Rent. India's most trusted construction equipment partner — proudly serving Bihar since 2016."}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/products" className="btn-primary" data-testid="hero-cta-products">
                Explore Fleet <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="btn-outline !border-white !text-white hover:!bg-white hover:!text-[#1A1A1A]" data-testid="hero-cta-contact">
                <Phone className="w-4 h-4" /> Talk to an Expert
              </Link>
            </div>
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-xl" data-testid="hero-stats">
              {[
                { n: settings?.stat_1_number || "500+", l: settings?.stat_1_label || "MACHINES DELIVERED" },
                { n: settings?.stat_2_number || "9+", l: settings?.stat_2_label || "YEARS IN BIHAR" },
                { n: settings?.stat_3_number || "₹100Cr+", l: settings?.stat_3_label || "FINANCE FACILITATED" },
              ].map((s)=>(
                <div key={s.l} className="border-l-4 border-[#E65C00] pl-4">
                  <div className="font-heading font-black text-2xl sm:text-3xl text-[#FFCC00]">
                    <AnimatedNumber value={s.n} />
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-400 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ BRAND MARQUEE (editable) ============ */}
      <div className="bg-[#FFCC00] py-4 border-y-4 border-[#1A1A1A]" data-testid="brand-marquee">
        <Marquee speed={40} gradient={false} className="marquee-fade">
          {marquee.map((b, i) => (
            <div key={i} className="mx-10 font-heading font-black text-[#1A1A1A] text-xl uppercase tracking-tighter">
              {b} <span className="text-[#E65C00] mx-4">★</span>
            </div>
          ))}
        </Marquee>
      </div>

      {/* ============ TRUST / WHO WE ARE ============ */}
      <Section className="py-20" data-testid="home-who-we-are">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <motion.div {...fadeUp} className="relative">
            {aboutImage ? (
              <img src={mediaUrl(aboutImage)} alt="About Tara Autohub" className="w-full h-[540px] object-cover" />
            ) : (
              <div className="w-full h-[540px] bg-[#1A1A1A] relative overflow-hidden flex items-center justify-center" data-testid="home-about-placeholder">
                <div className="absolute inset-0 grid-bg opacity-30" />
                <div className="absolute inset-0 noise-overlay" />
                <div className="relative text-center px-6">
                  <div className="font-heading font-black text-6xl text-[#E65C00] opacity-30">TARA</div>
                  <div className="text-xs uppercase tracking-[0.3em] text-gray-500 mt-3">Add an "About Band Image" in Settings</div>
                </div>
              </div>
            )}
            <div className="absolute -bottom-6 -left-6 bg-[#FFCC00] p-5 border-4 border-[#1A1A1A] max-w-[260px]" data-testid="home-about-badge" style={{ display: settings?.about_badge_show === false ? 'none' : undefined }}>
              <Award className="w-7 h-7 text-[#1A1A1A]" />
              <div className="font-heading font-black text-[#1A1A1A] text-sm mt-3 uppercase leading-tight">{settings?.about_badge || "Authorised Dealer · Bull Machines"}</div>
            </div>
            <div className="absolute -top-6 -right-6 bg-[#1A1A1A] text-white p-5 max-w-[240px] hidden sm:block" data-testid="home-about-founded" style={{ display: settings?.about_founded_show === false ? 'none' : undefined }}>
              <div className="font-heading font-black text-3xl text-[#E65C00]">{settings?.about_founded_year || "2016"}</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-gray-400 mt-1">{settings?.about_founded_label || "YEAR FOUNDED"}</div>
            </div>
          </motion.div>
          <motion.div {...fadeUp}>
            <div className="label-eyebrow">{settings?.about_eyebrow || "PARTNERING TRUST"}</div>
            <h2 className="heading-xl text-3xl sm:text-4xl lg:text-5xl mt-4 max-w-xl" data-testid="home-about-title">
              {highlightTitle(settings?.about_title || "BIHAR'S MOST TRUSTED EQUIPMENT CHANNEL PARTNER.")}
            </h2>
            <p className="mt-6 text-gray-700 leading-relaxed text-base sm:text-lg max-w-lg">
              {settings?.about_subtitle || "Incorporated in April 2016 by a team of young and dynamic entrepreneurs, Tara Autohub has grown into a trusted name in construction equipment dealing — with offices in Patna, Begusarai and Purnia."}
            </p>
            <div className="mt-7 grid sm:grid-cols-2 gap-3 max-w-lg" data-testid="home-about-bullets">
              {(settings?.about_band_bullets && settings.about_band_bullets.length > 0
                ? settings.about_band_bullets
                : ["Authorised Bull Machines dealer", "Multi-brand inventory: JCB, Tata, Volvo, CAT", "8+ NBFC & bank tie-ups", "Pan-Bihar service team"]
              ).map((p) => (
                <div key={p} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-[#E65C00] mt-0.5 shrink-0" />
                  <span className="text-gray-800 font-medium">{p}</span>
                </div>
              ))}
            </div>
            <Link to="/about" className="btn-primary mt-8 inline-flex" data-testid="who-we-are-cta">
              Our Story <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </Section>

      {/* ============ INDUSTRIES (editable) ============ */}
      <section className="bg-[#1A1A1A] text-white relative overflow-hidden" data-testid="home-industries">
        <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
        <Section className="py-20 relative">
          <div className="max-w-3xl">
            <div className="label-eyebrow">{settings?.applications_eyebrow || "APPLICATIONS"}</div>
            <h2 className="heading-xl text-3xl sm:text-4xl lg:text-5xl mt-4 text-white" data-testid="home-apps-title">
              {settings?.applications_title || "FROM RAILWAYS TO HIGH-RISES. WE EQUIP THEM ALL."}
            </h2>
            <p className="mt-5 text-gray-400 max-w-2xl">
              {settings?.applications_subtitle || "Our equipment serves every link of India's infrastructure boom — from road construction crews to large-scale mining operators and urban developers."}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px mt-12 bg-white/10">
            {apps.map((title, i) => {
              const Icon = ICON_MAP[i % 4];
              return (
                <motion.div key={`${title}-${i}`} {...fadeUp} transition={{ duration: 0.5, delay: i*0.08 }}
                  className="bg-[#1A1A1A] p-8 group hover:bg-[#E65C00] transition-colors duration-300"
                >
                  <Icon className="w-9 h-9 text-[#E65C00] group-hover:text-white transition-colors" />
                  <div className="font-heading font-black text-lg uppercase mt-5 leading-tight">{title}</div>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </section>

      {/* ============ DID YOU KNOW (single-line stat band) ============ */}
      {settings?.didyouknow_text && (
        <section className="bg-[#FFCC00] py-10" data-testid="home-did-you-know">
          <Section>
            <div className="max-w-5xl mx-auto text-center">
              <div className="label-eyebrow !text-[#1A1A1A]">{settings?.didyouknow_eyebrow || "DID YOU KNOW"}</div>
              <p className="font-heading font-black uppercase text-[#1A1A1A] text-xl sm:text-2xl lg:text-3xl mt-3 leading-tight">
                {settings.didyouknow_text}
              </p>
            </div>
          </Section>
        </section>
      )}

      {/* ============ CORPORATE VIDEO ============ */}
      <CorporateVideoBand settings={settings} />

      {/* ============ FEATURED PRODUCTS (admin-driven via "Featured" checkbox on Products) ============ */}
      <Section className="py-20" data-testid="home-featured-section">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionTitle eyebrow="FEATURED PRODUCTS" title={<>OUR PICKS FOR THIS <span className="text-[#E65C00]">SEASON.</span></>} sub="Hand-picked machines our dealership team is currently championing. Toggle the 'Featured' switch in admin to add or remove products from this band." />
          <Link to="/products" className="btn-outline" data-testid="featured-view-all">
            Full Catalog <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {featured === null
            ? Array.from({ length: 3 }).map((_, i) => <div key={i} className="skeleton aspect-[4/5]" />)
            : featured.length === 0
              ? <EmptyState title="Featured Products Coming Soon" sub="Add products in the admin panel and mark them as 'Featured' to showcase them here." />
              : featured.map((p) => <ProductCard key={p.id} p={{ ...p, image: mediaUrl(p.image) }} />)}
        </div>
      </Section>

      {/* ============ TESTIMONIALS (after Featured Products) ============ */}
      <TestimonialsBand settings={settings} />

      {/* ============ SERVICES BENTO ============ */}
      <section className="bg-[#0F0F0F] text-white py-24 relative overflow-hidden noise-overlay">
        <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
        <Section>
          <SectionTitle eyebrow={settings?.home_services_eyebrow || "WHAT WE DO"} title={<span className="text-white">{settings?.home_services_title || "EVERY SOLUTION. ONE ROOF."}</span>} sub={<span className="text-gray-400">{settings?.home_services_subtitle || "From financing to after-sales, we handle every link in your equipment value chain."}</span>} />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px mt-12 bg-white/10">
            {services.map((s, i) => (
              <motion.div key={s.title} {...fadeUp} transition={{ duration: 0.5, delay: i*0.08 }}
                className="bg-[#1A1A1A] p-8 group hover:bg-[#E65C00] transition-colors duration-300 cursor-pointer"
                data-testid={`service-card-${i}`}
              >
                <s.icon className="w-9 h-9 text-[#E65C00] group-hover:text-white" />
                <h3 className="font-heading font-black text-xl uppercase mt-6 leading-tight">{s.title}</h3>
                <p className="text-sm text-gray-400 group-hover:text-white/90 mt-3 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/services" className="inline-flex btn-yellow">See All Services <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </Section>
      </section>

      {/* ============ INSIGHTS ============ */}
      <section className="bg-gray-50 py-20" data-testid="home-blog-section">
        <Section>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionTitle eyebrow="LATEST INSIGHTS" title="INDUSTRY READING." sub="Buying guides, market updates and finance strategy from the people who sell the steel." />
            <Link to="/blog" className="btn-outline">All Articles <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {latestBlogs === null
              ? Array.from({ length: 3 }).map((_, i) => <div key={i} className="skeleton aspect-[4/3]" />)
              : latestBlogs.length === 0
                ? <EmptyState title="Insights Coming Soon" sub="Publish a blog post or podcast episode from the admin panel and it will appear here automatically." />
                : latestBlogs.map((b) => (
                <Link key={b.id} to={`/blog/${b.slug}`} className="card-industrial block group" data-testid={`blog-card-${b.id}`}>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={mediaUrl(b.cover_image)} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <div className="label-eyebrow !text-gray-500">{b.category}</div>
                    <h3 className="font-heading font-black text-lg leading-tight mt-2 group-hover:text-[#E65C00]">{b.title}</h3>
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{b.excerpt}</p>
                  </div>
                </Link>
              ))}
          </div>
        </Section>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="bg-[#1A1A1A] py-20 relative overflow-hidden noise-overlay" data-testid="home-final-cta">
        <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
        <Section className="relative">
          <div className="grid md:grid-cols-12 items-center gap-8">
            <div className="md:col-span-8">
              <div className="label-eyebrow">CONSULTATION</div>
              <h2 className="heading-xl text-3xl sm:text-4xl lg:text-5xl text-white mt-4" data-testid="final-cta-title">
                {settings?.final_cta_title || "READY TO MOVE EARTH?"}
              </h2>
              <p className="text-gray-400 mt-4 max-w-xl text-base sm:text-lg">
                {settings?.final_cta_subtitle || "Free site visit, free demo, free finance consultation. Talk to a Tara Autohub expert today."}
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#E65C00] text-white font-heading font-black uppercase tracking-widest text-xs px-5 py-2.5 hover:bg-[#CC5200] transition-colors" data-testid="final-cta-button">
                {settings?.final_cta_button || "Book a Consultation"} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Section>
      </section>
    </>
  );
}
